import React from "react";
import { Box, TextField, TextFieldProps } from "@mui/material";
import { LocalizationProvider, DesktopDatePicker, DateValidationError } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { styled } from "@mui/material/styles";
import { Dayjs } from "dayjs";
import { StyledInputLabel } from "../modules/fiscalUpgrade/StyledComponents/Container";

interface CustomDatePickerProps extends Omit<TextFieldProps, "onChange" | "defaultValue"> {
  readOnly?: boolean;
  error?: boolean;
  labelText?: string;
  required?: boolean;
  value: Dayjs | null;
  onChange?: (newValue: Dayjs | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  slots?: any; // <-- Add this
  slotProps?: any; // <-- Optionally this too if needed
  maxDate?: Dayjs;
  minDate?: Dayjs;
  [key: string]: any
}

const InputForDatePicker = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "readOnly",
})<{ readOnly?: boolean; error?: boolean }>(({ theme, readOnly, error }) => ({
  '& .MuiInputBase-root.Mui-readOnly': {
    backgroundColor: 'white',
    color: 'black',
  },
  '& .Mui-readOnly .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  "& .MuiOutlinedInput-root": {
    ...(readOnly && {
      backgroundColor: "#F5F5F5",
    }),
    "&.Mui-disabled": {
      backgroundColor: "#F5F5F5",
      color: "#757575",
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#757575", // greyed text color when disabled
  },
  "& .MuiInputBase-input": {
    fontSize: "17.67px",
    fontWeight: 400,
    color: readOnly ? "#757575" : "#2C2222",
    letterSpacing: "0.27px",
    height: "23.5px",
    fontFamily: ["Inter", "sans-serif"],
  },
  "& .MuiFormHelperText-root": {
    color: error ? "red" : theme.palette.text.secondary,
  },
}));


const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  labelText,
  required,
  value,
  onChange,
  onBlur, // <-- DESTRUCTURE onBlur here
  maxDate,
  minDate,
  error,
  helperText,
  readOnly = false,
  variant = "outlined",
  fullWidth = true,
  slots,
  slotProps,
  ...props // <-- other props
}) => {

  const handleError = (error: DateValidationError, value: Dayjs | null) => {
    console.error("Date validation error:", error, value);
  };

  return (
    <Box>
      {labelText && (
        <StyledInputLabel shrink>
          {labelText} {required && <span style={{ color: "red" }}>*</span>}
        </StyledInputLabel>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          {...props}
          value={value}
          onChange={(newValue: Dayjs | null) => {
            if (onChange) {
              onChange(newValue);
            }
          }}
          onError={handleError}
          disabled={readOnly}
          maxDate={maxDate}
          minDate={minDate}
          slots={{
            textField: InputForDatePicker,
            ...slots,
          }}
          slotProps={{
            textField: {
              size: "small",
              error,
              helperText,
              disabled: readOnly,
              variant,
              fullWidth,
              onBlur: onBlur, // <-- now no error
            },
            ...slotProps,
          }}
        /> 
      </LocalizationProvider>
    </Box>
  );
};

export default CustomDatePicker;
